import axios from 'axios';
import { addBreadcrumb, captureException, captureAPIError } from '@/lib/sentry';
import { clientLogger } from '@/lib/clientLogger';
import { redactForApiTelemetry } from '@/lib/pii';

// Configuración base de axios
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para añadir el token JWT a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Agregar breadcrumb de Sentry con información del request
    addBreadcrumb(
      `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      'http',
      {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        // Headers (sin token por seguridad)
        headers: Object.fromEntries(
          Object.entries(config.headers || {}).filter(
            ([key]) => !['authorization', 'cookie'].includes(key.toLowerCase())
          )
        ),
        // Payload del request (limitado a 1000 caracteres)
        data: config.data
          ? JSON.stringify(
              redactForApiTelemetry(config.url, config.data)
            ).substring(0, 1000)
          : undefined,
        params: config.params,
      },
      'info'
    );

    // Log local del request (independiente de Sentry)
    clientLogger.logRequest(
      config.method || 'GET',
      config.url || '',
      redactForApiTelemetry(config.url, config.data),
      config.headers
    );

    return config;
  },
  (error) => {
    // Capturar errores antes de enviar el request
    captureException(new Error('Request interceptor error'), {
      operation: 'axios-request-interceptor',
      extra: {
        error: error.message,
        stack: error.stack,
      }
    });
    return Promise.reject(error);
  }
);

// Variable para controlar si ya estamos refrescando el token
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Interceptor para manejar errores de respuesta y refresh automático
api.interceptors.response.use(
  (response) => {
    // Agregar breadcrumb de respuesta exitosa
    addBreadcrumb(
      `API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
      'http',
      {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        statusText: response.statusText,
        // Response headers (limitado)
        response_headers: response.headers ?
          Object.fromEntries(
            Object.entries(response.headers).slice(0, 10)
          ) : undefined,
        // Response data (limitado a 1000 caracteres)
        response_data: response.data
          ? JSON.stringify(
              redactForApiTelemetry(response.config.url, response.data)
            ).substring(0, 1000)
          : undefined,
      },
      'info'
    );

    // Log local de respuesta exitosa
    clientLogger.logResponse(
      response.config.method || 'GET',
      response.config.url || '',
      response.status,
      redactForApiTelemetry(response.config.url, response.data)
    );

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log del error para debugging
    console.error('API Error:', {
      message: error.message,
      response: redactForApiTelemetry(
        error.config?.url,
        error.response?.data
      ),
      status: error.response?.status,
      config: {
        method: error.config?.method,
        url: error.config?.url,
        baseURL: error.config?.baseURL
      }
    });

    // Agregar breadcrumb detallado del error
    addBreadcrumb(
      `API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'Network Error'}`,
      'http',
      {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        status: error.response?.status,
        statusText: error.response?.statusText,
        // Request data que causó el error
        request_data: error.config?.data
          ? JSON.stringify(
              redactForApiTelemetry(error.config?.url, error.config.data)
            ).substring(0, 1000)
          : undefined,
        // Response error data
        error_response: error.response?.data
          ? JSON.stringify(
              redactForApiTelemetry(error.config?.url, error.response.data)
            ).substring(0, 1000)
          : undefined,
        // Error message
        error_message: error.message,
        // Si es error de red
        is_network_error: !error.response,
      },
      'error'
    );

    // Capturar TODOS los errores en Sentry (incluyendo 401)
    // Opción 1: Captura completa para debugging exhaustivo
    captureAPIError(error, {
      component: 'axios-response-interceptor',
      user_action: 'api_call_failed',
      // Marcar si es un error esperado del flujo de auth
      is_auth_flow: error.response?.status === 401
    });

    // Log local del error (independiente de Sentry)
    clientLogger.logError(
      error.config?.method || 'UNKNOWN',
      error.config?.url || 'UNKNOWN',
      error
    );

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Si ya estamos refrescando, agregamos esta petición a la cola
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // No hay refresh token, redirigir al login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(error);
      }

      try {
        // Intentar refrescar el token
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Actualizar tokens en localStorage
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('user', JSON.stringify(response.data.data));

        // Actualizar el header de autorización para la petición original
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Procesar la cola de peticiones fallidas
        processQueue(null, accessToken);

        // Reintentar la petición original
        return api(originalRequest);

      } catch (refreshError) {
        // Error al refrescar el token, limpiar todo y redirigir
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // Agregar información adicional para mejor manejo de errores
    if (!error.response && error.request) {
      error.code = 'NETWORK_ERROR';
      error.message = 'Error de conexión con el servidor';
    }
    
    return Promise.reject(error);
  }
);

export default api; 